/**
 * CreditPotentialPanel — SME Carbon Credit Component
 * Shows credit potential, value range, and CTA to issue credits.
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Leaf, RefreshCw, ArrowRight, Check, AlertTriangle, ExternalLink, TrendingUp, Users, Star } from "lucide-react";

const fmt   = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";
const fmtK  = (n) => !n ? "—" : n >= 1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n)}`;
const GRADE_COLORS = { excellent:"text-emerald-700 bg-emerald-50", good:"text-blue-700 bg-blue-50", moderate:"text-amber-700 bg-amber-50", low:"text-slate-500 bg-slate-100" };
const VERIF_CONFIG = {
  analyst_reviewed:     { color:"text-indigo-600", label:"Analyst Reviewed" },
  third_party_verified: { color:"text-emerald-600", label:"3rd Party Verified" },
  system_validated:     { color:"text-blue-600",   label:"System Validated" },
  self_reported:        { color:"text-slate-500",  label:"Self-Reported" },
};

export default function CreditPotentialPanel({ assessmentId, mode = "panel" }) {
  const [potential, setPotential]   = useState(null);
  const [credits,   setCredits]     = useState([]);
  const [pricing,   setPricing]     = useState(null);
  const [matches,   setMatches]     = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [issuing,   setIssuing]     = useState(false);
  const [listing,   setListing]     = useState(false);
  const [price,     setPrice]       = useState("10");
  const [showList,  setShowList]    = useState(false);
  const [showMatch, setShowMatch]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.allSettled([
        client.get(`/credits/potential/${assessmentId}`),
        client.get(`/credits/assessment/${assessmentId}`),
      ]);
      if (pRes.status === "fulfilled") setPotential(pRes.value.data);
      if (cRes.status === "fulfilled") {
        const creditData = cRes.value.data?.credits || [];
        setCredits(creditData);
        // Load pricing + buyer matching for first issued credit
        const firstIssued = creditData.find(c => c.status !== "retired");
        if (firstIssued) {
          const [prRes, mRes] = await Promise.allSettled([
            client.get(`/credits/pricing/${firstIssued.credit_id}`),
            client.get(`/marketplace/match/${firstIssued.credit_id}`),
          ]);
          if (prRes.status === "fulfilled") { setPricing(prRes.value.data); setPrice(String(Math.round(prRes.value.data.recommended_price || 10))); }
          if (mRes.status === "fulfilled")  setMatches(mRes.value.data?.top_segments || []);
        }
      }
    } catch {}
    setLoading(false);
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const handleIssue = async () => {
    setIssuing(true);
    try {
      const res = await client.post(`/credits/generate/${assessmentId}`);
      toast.success(`${res.data.volume_tco2e?.toFixed(2)} tCO₂e credits issued! ID: ${res.data.credit_id}`);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : detail || "Credit issuance failed");
    }
    setIssuing(false);
  };

  const handleList = async () => {
    const creditId = credits[0]?.credit_id;
    if (!creditId || !price) return;
    setListing(true);
    try {
      await client.post("/marketplace/list", { credit_id: creditId, price_per_tonne: Number(price), description: "DPCS+ verified carbon credits" });
      toast.success("Credits listed on marketplace!");
      setShowList(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Listing failed"); }
    setListing(false);
  };

  if (loading) return <div className="h-32 bg-slate-100 rounded-xl animate-pulse" />;
  if (!potential && !credits.length) return null;

  const issued = credits.filter(c => c.status !== "retired");
  const hasIssued = issued.length > 0;
  const p = potential || {};

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="credit-potential-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
            <Leaf size={14} className="text-[#059669]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#0F172A]">Carbon Credits</h3>
            <p className="text-xs text-slate-400">DPCS+ {p.methodology || "Protocol"}</p>
          </div>
        </div>
        <button onClick={load} className="text-slate-300 hover:text-slate-500"><RefreshCw size={12} /></button>
      </div>

      {hasIssued ? (
        <div className="space-y-3">
          {issued.map(c => (
            <div key={c.credit_id} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-bold text-emerald-700 font-mono">{c.credit_id}</p>
                  <p className="text-sm font-bold text-[#0F172A]">{fmt(c.volume_tco2e, 2)} tCO₂e</p>
                  <p className="text-xs text-slate-400">{c.methodology_name} · Vintage {c.vintage_year}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Est. value</p>
                  <p className="text-sm font-bold text-[#059669]">{fmtK(c.value_estimate_usd)}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full mt-1 inline-block ${c.status === "listed" ? "bg-blue-50 text-blue-700" : c.status === "retired" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                    {c.status}
                  </span>
                </div>
              </div>
              {c.status === "issued" && (
                !showList ? (
                  <button onClick={() => setShowList(true)}
                    className="mt-2 w-full text-xs text-[#059669] font-medium flex items-center justify-center gap-1 hover:text-emerald-700">
                    <ArrowRight size={11} /> List on Marketplace
                  </button>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <input type="number" value={price} onChange={e=>setPrice(e.target.value)} placeholder="Price USD/t"
                      className="flex-1 h-8 px-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#059669]" />
                    <button onClick={handleList} disabled={listing} className="px-3 h-8 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                      {listing ? "..." : "List"}
                    </button>
                    <button onClick={() => setShowList(false)} className="px-2 h-8 text-slate-400 text-xs rounded-lg hover:bg-slate-50">✕</button>
                  </div>
                )
              )}
              <a href={`/api/credits/${c.credit_id}`} target="_blank" rel="noopener noreferrer"
                className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600">
                <ExternalLink size={9} /> View in registry
              </a>
              {/* Pricing recommendation */}
              {pricing && (
                <div className="mt-3 pt-3 border-t border-emerald-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pricing Engine</p>
                    <span className="text-xs text-slate-400">{pricing.confidence} confidence</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-base font-bold text-[#0F172A]">${pricing.recommended_price}</p>
                      <p className="text-[10px] text-slate-400">{pricing.price_range?.label}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Total value range</p>
                      <p className="text-xs font-bold text-[#059669]">{fmtK(pricing.total_value_range?.low_usd)}–{fmtK(pricing.total_value_range?.high_usd)}</p>
                    </div>
                  </div>
                  {pricing.drivers?.slice(0,2).map((d,i) => (
                    <div key={i} className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                      <span>{d.factor}</span><span className="font-semibold">{d.impact}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Buyer matches */}
              {matches.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-100">
                  <button onClick={() => setShowMatch(!showMatch)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2">
                    <Users size={11} /> {showMatch ? "Hide" : "Show"} buyer matches ({matches.length})
                  </button>
                  {showMatch && (
                    <div className="space-y-1.5">
                      {matches.slice(0,3).map((m,i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-[10px] text-slate-500 truncate flex-1">{m.segment_name}</span>
                          <div className="flex items-center gap-1 ml-2">
                            <div className="w-12 h-1 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{width:`${m.match_score}%`}}/></div>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${GRADE_COLORS[m.grade]}`}>{m.match_score}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-400">Est. Credits</p>
              <p className="text-lg font-bold text-[#0F172A] tabular-nums">{fmt(p.estimated_credits, 1)}</p>
              <p className="text-[10px] text-slate-400">tCO₂e</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-400">Est. Value</p>
              <p className="text-sm font-bold text-[#059669]">{fmtK(p.value_range?.low_usd)}–{fmtK(p.value_range?.high_usd)}</p>
              <p className="text-[10px] text-slate-400">$5–$15/t</p>
            </div>
          </div>
          {p.is_eligible ? (
            <button onClick={handleIssue} disabled={issuing} data-testid="issue-credits-btn"
              className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50">
              {issuing ? <RefreshCw size={13} className="animate-spin" /> : <Leaf size={13} />}
              {issuing ? "Issuing..." : "Issue Credits"}
            </button>
          ) : (
            <div className="border border-amber-200 bg-amber-50 rounded-xl p-3">
              <div className="flex items-start gap-2 text-xs text-amber-700">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                <p>{p.gap_to_eligibility || "Complete verification to enable credit issuance."}</p>
              </div>
            </div>
          )}
          <p className="text-[10px] text-slate-400 text-center italic">
            Credits require project validation before issuance. Estimate only.
          </p>
        </div>
      )}
    </div>
  );
}
